import { OnboardingLayout } from "@bcc/components/onboarding-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { toast } from "@bcc/hooks/use-toast";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { InstallationsContext, type CompleteShopifyOAuthFailureReason } from "@client-shared/providers/installations";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

type CompleteShopifyOAuthMethod = (params: {
  code: string;
  state: string;
  shop: string;
}) => Promise<{ success: boolean; error?: Error; reason?: CompleteShopifyOAuthFailureReason }>;

type InstallationMethods = {
  completeShopifyOAuth?: CompleteShopifyOAuthMethod;
};

const SHOPIFY_CALLBACK_TEST_ID_PREFIX = "shopify-callback";

const setShopifyCallbackTestId = (suffix: string) => `${SHOPIFY_CALLBACK_TEST_ID_PREFIX}-${suffix}`;

export function ShopifyCallbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [asyncErrorReason, setAsyncErrorReason] = useState<CompleteShopifyOAuthFailureReason | undefined>(undefined);
  const [asyncErrorMessage, setAsyncErrorMessage] = useState<string | undefined>(undefined);
  const hasStartedRef = useRef(false);
  const toastedReasonRef = useRef<CompleteShopifyOAuthFailureReason | undefined>(undefined);

  const [, , installationMethodsRaw] = useContext(InstallationsContext);
  const installationMethods = installationMethodsRaw as unknown as InstallationMethods | undefined;
  const completeShopifyOAuth = installationMethods?.completeShopifyOAuth;

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const shop = searchParams.get("shop");
  const oauthError = searchParams.get("error");

  let syncErrorReason: CompleteShopifyOAuthFailureReason | undefined;
  if (oauthError) {
    syncErrorReason = "userDenied";
  } else if (!code || !state || !shop || !completeShopifyOAuth) {
    syncErrorReason = "generic";
  }

  const errorReason = syncErrorReason ?? asyncErrorReason;
  // "backendError" is a generic marker reason; when installShopifyService resolved to a more
  // specific, already-translated message (e.g. a missing-scope or invalid-token explanation
  // from buildInstallShopifyError), show that instead of the generic static copy. Every other
  // reason here is derived client-side (session/state/shop checks) and keeps its own dedicated,
  // already-appropriate translation.
  const errorMessage =
    errorReason === "backendError" && asyncErrorMessage
      ? asyncErrorMessage
      : t(`onboarding.shopDomainModal.shopify.callback.error.${errorReason}`);

  useEffect(() => {
    if (errorReason && toastedReasonRef.current !== errorReason) {
      toastedReasonRef.current = errorReason;
      toast({
        variant: "destructive",
        title: t("onboarding.shopDomainModal.shopify.callback.error.title"),
        description: errorMessage,
      });
    }
  }, [errorReason, errorMessage, t]);

  useEffect(() => {
    if (hasStartedRef.current) return;
    if (syncErrorReason) return;
    if (!completeShopifyOAuth || !code || !state || !shop) return;
    hasStartedRef.current = true;

    void (async () => {
      const result = await completeShopifyOAuth({ code, state, shop });
      if (result.success) {
        navigate(ROUTE_PATHS.ONBOARDING_STEP_2, { replace: true });
        return;
      }
      setAsyncErrorReason(result.reason ?? "backendError");
      setAsyncErrorMessage(result.error?.message);
    })();
  }, [code, completeShopifyOAuth, navigate, shop, state, syncErrorReason]);

  const handleReturn = useCallback(() => {
    navigate(ROUTE_PATHS.ONBOARDING, { replace: true });
  }, [navigate]);

  if (errorReason) {
    return (
      <OnboardingLayout testId={setShopifyCallbackTestId("container-error")}>
        <Card className="w-full max-w-2xl mx-auto border-border-subtle shadow-sm">
          <CardHeader className="text-center">
            <CardTitle data-testid={setShopifyCallbackTestId("error-title")} className="text-2xl font-semibold text-text-primary">
              {t("onboarding.shopDomainModal.shopify.callback.error.title")}
            </CardTitle>
            <CardDescription data-testid={setShopifyCallbackTestId("error-description")} className="text-base text-text-muted">
              {errorMessage}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-8">
            <button
              type="button"
              onClick={handleReturn}
              data-testid={setShopifyCallbackTestId("return")}
              className="rounded-md border border-border bg-white px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              {t("onboarding.shopDomainModal.shopify.callback.returnToOnboarding")}
            </button>
          </CardContent>
        </Card>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout testId={setShopifyCallbackTestId("container")}>
      <Card className="w-full max-w-2xl mx-auto border-border-subtle shadow-sm">
        <CardHeader className="text-center">
          <CardTitle data-testid={setShopifyCallbackTestId("title")} className="text-2xl font-semibold text-text-primary">
            {t("onboarding.shopDomainModal.shopify.callback.authorizing")}
          </CardTitle>
          <CardDescription data-testid={setShopifyCallbackTestId("description")} className="text-base text-text-muted">
            {t("onboarding.shopDomainModal.shopify.callback.pleaseWait")}
          </CardDescription>
        </CardHeader>
      </Card>
    </OnboardingLayout>
  );
}
